/**
 * E9. Акции: в конверте с 1990 года, с 18 лет. Ожидание +5 % в год при
 * σ 18 % (индекс; логнормальный рост считает движок раз в год). Это не
 * азарт: тяги нет, и игра говорит это прямо (`minigame.expect.stocks`).
 * `{ amount }` покупает (не больше денег), `{ sell: true }` продаёт всю
 * позицию по текущей стоимости (`flags.stocksHeld`), в любой год.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import { FLAG_STOCKS_HELD, STOCKS_MEAN, STOCKS_SIGMA, type Life, type MinigameOutcome } from '@life/engine';
import type { ExpectancyText, MinigameDef } from '../types';
import { clampBet, noPlay } from '../casino/common';
import { ADULT_AGE, heldOf } from './common';

export const STOCKS_FIRST_YEAR = 1990;

export interface StocksState {
  /** Текущая стоимость позиции (0, если акций нет). */
  held: number;
  maxAmount: number;
  meanGain: number;
  sigma: number;
}

export type StocksInput = { amount: number } | { sell: true };

export const stocks: MinigameDef<StocksInput, StocksState> = {
  id: 'stocks',
  section: 'E',
  triggers: [{ kind: 'envelope' }],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    if (life.age < ADULT_AGE || life.birthYear + life.age < STOCKS_FIRST_YEAR) return false;
    return life.money > 0 || heldOf(life, FLAG_STOCKS_HELD) > 0;
  },
  setup(life: Life): StocksState {
    return { held: heldOf(life, FLAG_STOCKS_HELD), maxAmount: Math.max(0, life.money), meanGain: STOCKS_MEAN - 1, sigma: STOCKS_SIGMA };
  },
  play(state: StocksState, input: StocksInput, life: Life): MinigameOutcome {
    const held = heldOf(life, FLAG_STOCKS_HELD);
    if ('sell' in input) {
      if (held <= 0) return noPlay();
      return { moneyDelta: held, urgeDelta: 0, flags: { [FLAG_STOCKS_HELD]: 0 }, logKey: 'minigame.stocks.sell', logParams: { amount: Math.round(held) } };
    }
    const amount = clampBet(Math.min(input.amount, state.maxAmount), life.money);
    if (amount <= 0) return noPlay();
    return {
      moneyDelta: -amount,
      urgeDelta: 0,
      flags: { [FLAG_STOCKS_HELD]: held + amount },
      logKey: 'minigame.stocks.buy',
      logParams: { amount: Math.round(amount) },
    };
  },
  expected(): MinigameOutcome {
    return noPlay();
  },
  expectancyText: (state: StocksState): ExpectancyText => ({
    key: 'minigame.expect.stocks',
    params: { gain: Math.round(state.meanGain * 100), sigma: Math.round(state.sigma * 100) },
  }),
};
